import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [centers, setCenters] = useState([]);
    const [bookings, setBookings] = useState([]);

    const refresh = () => {
        api.get("/admin/stats").then((r) => setStats(r.data));
        api.get("/centers/all").then((r) => setCenters(r.data));
        api.get("/bookings/all").then((r) => setBookings(r.data));
    };
    useEffect(refresh, []);

    const setStatus = async (id, status) => {
        await api.patch(`/centers/${id}/status`, { status });
        toast.success(`Center ${status}`);
        refresh();
    };

    return (
        <div className="mx-auto max-w-7xl px-6 py-10">
            <div className="overline mb-2">Super admin</div>
            <h1 className="font-heading text-4xl font-bold tracking-tight">Platform control</h1>

            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mt-8">
                <Stat label="Students" value={stats?.total_students ?? "—"} />
                <Stat label="Center Owners" value={stats?.total_owners ?? "—"} />
                <Stat label="Centers" value={stats?.total_centers ?? "—"} />
                <Stat label="Approved" value={stats?.approved_centers ?? "—"} />
                <Stat label="Pending" value={stats?.pending_centers ?? "—"} accent />
                <Stat label="Revenue" value={stats ? `₹${(stats.revenue ?? 0).toFixed(2)}` : "—"} />
            </div>

            <Tabs defaultValue="pending" className="mt-10">
                <TabsList className="rounded-sm bg-secondary">
                    <TabsTrigger value="pending" data-testid="admin-tab-pending">Pending centers</TabsTrigger>
                    <TabsTrigger value="all" data-testid="admin-tab-all">All centers</TabsTrigger>
                    <TabsTrigger value="bookings" data-testid="admin-tab-bookings">Bookings</TabsTrigger>
                </TabsList>

                <TabsContent value="pending">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead><TableHead>City</TableHead><TableHead>Seats</TableHead><TableHead>Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {centers.filter((c) => c.status === "pending").map((c) => (
                                <TableRow key={c.id} data-testid={`admin-pending-${c.id}`}>
                                    <TableCell>{c.name}<div className="text-xs text-muted-foreground">{c.address}</div></TableCell>
                                    <TableCell>{c.city}</TableCell>
                                    <TableCell className="font-mono">{c.total_seats}</TableCell>
                                    <TableCell className="flex gap-2">
                                        <Button size="sm" onClick={() => setStatus(c.id, "approved")} className="rounded-sm bg-primary hover:bg-primary/90" data-testid={`approve-${c.id}`}>Approve</Button>
                                        <Button size="sm" variant="outline" onClick={() => setStatus(c.id, "rejected")} className="rounded-sm" data-testid={`reject-${c.id}`}>Reject</Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {centers.filter((c) => c.status === "pending").length === 0 && (
                                <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground">Inbox zero. All caught up.</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TabsContent>

                <TabsContent value="all">
                    <Table>
                        <TableHeader>
                            <TableRow><TableHead>Name</TableHead><TableHead>City</TableHead><TableHead>Seats</TableHead><TableHead>Status</TableHead></TableRow>
                        </TableHeader>
                        <TableBody>
                            {centers.map((c) => (
                                <TableRow key={c.id}>
                                    <TableCell>{c.name}</TableCell>
                                    <TableCell>{c.city}</TableCell>
                                    <TableCell className="font-mono">{c.total_seats}</TableCell>
                                    <TableCell className="uppercase tracking-widest text-xs">{c.status}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TabsContent>

                <TabsContent value="bookings">
                    <Table>
                        <TableHeader>
                            <TableRow><TableHead>Code</TableHead><TableHead>Student</TableHead><TableHead>Center</TableHead><TableHead>Date</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead></TableRow>
                        </TableHeader>
                        <TableBody>
                            {bookings.map((b) => (
                                <TableRow key={b.id}>
                                    <TableCell className="font-mono">{b.booking_code}</TableCell>
                                    <TableCell>{b.student_name}</TableCell>
                                    <TableCell>{b.center_name}</TableCell>
                                    <TableCell className="font-mono">{b.exam_date}</TableCell>
                                    <TableCell className="font-mono">₹{b.price.toFixed(2)}</TableCell>
                                    <TableCell className="uppercase tracking-widest text-xs">{b.status}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TabsContent>
            </Tabs>
        </div>
    );
}

function Stat({ label, value, accent }) {
    return (
        <div className={`border p-5 ${accent ? "border-primary bg-primary/5" : "border-border bg-card"}`}>
            <div className="overline">{label}</div>
            <div className="font-heading text-3xl font-bold mt-1">{value}</div>
        </div>
    );
}
